import React, { useState, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/database';
import { Link, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import { useUI } from '../context/UIContext';
import { deleteProposalSafely, pushWorkspaceBackup } from '../services/cloudBackup';
import { reserveDocumentNumber } from '../services/documentService';
import { rankProposalSearchResults } from '../components/search/ProposalCommandPalette';

const isPublishedStatus = (status) => status === 'Published' || status === 'Sent';

const incrementMinorVersion = (version) => {
  if (!version) return '1.0';
  const parts = version.split('.');
  const major = parseInt(parts[0]) || 1;
  const minor = parseInt(parts[1]) || 0;
  return `${major}.${minor + 1}`;
};

function validateSowName(value) {
  const trimmed = value.trim();
  if (!trimmed) return 'SOW name is required.';
  if (trimmed.length < 3 || trimmed.length > 120) return 'Name must be 3 to 120 characters.';
  if (!/^[A-Za-z0-9 _-]+$/.test(trimmed)) return 'Use letters, numbers, spaces, hyphens or underscores only.';
  if (!/[A-Za-z0-9]/.test(trimmed)) return 'Name must contain at least one letter or number.';
  return '';
}

function calculateTotal(proposal, settings) {
  const items = proposal?.financials?.items || [];
  const subtotal = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const taxRate = settings?.taxRate || 10;
  const taxEnabled = proposal?.financials?.taxEnabled;
  const taxAmount = taxEnabled ? subtotal * (taxRate / 100) : 0;
  return subtotal + taxAmount;
}

function calculateDeposit(proposal, total) {
  const milestones = proposal?.paymentMilestones || [];
  const depositMilestone = milestones.find(
    (m) => m.label && m.label.toLowerCase().includes('deposit') && (Number(m.percent) || 0) > 0
  );
  if (!depositMilestone) return null;
  const percent = Number(depositMilestone.percent) || 0;
  return { amount: (total * percent) / 100, percent };
}

function formatCurrency(amount, currency) {
  const validatedCurrency = currency || 'AUD';
  try {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: validatedCurrency }).format(amount || 0);
  } catch (e) {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);
  }
}

function normaliseTelHref(phone) {
  if (!phone || typeof phone !== 'string') return '';
  let cleaned = phone.replace(/[\s()\-\u2010\u2011\u2012\u2013\u2014]/g, '');
  if (!cleaned) return '';
  if (cleaned.startsWith('+61')) {
    if (/^\+61\d+$/.test(cleaned)) return cleaned;
    return '';
  }
  if (cleaned.startsWith('04')) {
    const rest = cleaned.slice(2);
    if (/^\d+$/.test(rest)) return '+614' + rest;
    return '';
  }
  if (cleaned.startsWith('614')) {
    const rest = cleaned.slice(3);
    if (/^\d+$/.test(rest)) return '+614' + rest;
    return '';
  }
  return '';
}

function getDisplayPhone(proposal) {
  return proposal.clientMobile || proposal.mobileNumber || proposal.phoneNumber || proposal.clientPhone || '';
}

const APPROVAL_BADGE_MAP = {
  PENDING: { label: 'Awaiting Approval', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  APPROVED: { label: 'Approved', className: 'bg-green-50 text-green-700 border-green-200' },
  CHANGES_REQUESTED: { label: 'Changes Requested', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  EXPIRED: { label: 'Expired', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  REVOKED: { label: 'Revoked', className: 'bg-red-50 text-red-700 border-red-200' },
  SEND_FAILED: { label: 'Send Failed', className: 'bg-red-50 text-red-700 border-red-200' },
};

const NO_APPROVAL_BADGE = {
  label: 'Not Sent',
  className: 'bg-slate-50 text-slate-500 border-slate-200',
};

function ApprovalBadge({ approval }) {
  const cfg = approval?.status ? APPROVAL_BADGE_MAP[approval.status] || NO_APPROVAL_BADGE : NO_APPROVAL_BADGE;
  return (
    <div className="mt-1.5 flex flex-col items-start gap-0.5">
      <span className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider border ${cfg.className}`}>
        {cfg.label}
      </span>
      {approval?.status === 'APPROVED' && approval.decisionAt && (
        <span className="text-[9px] text-gray-400 font-medium leading-none">
          {format(new Date(approval.decisionAt), 'MMM d, yyyy')}
        </span>
      )}
    </div>
  );
}

export default function ProposalList() {
  const navigate = useNavigate();
  const { confirm, showToast, promptText } = useUI();

  const proposals = useLiveQuery(() => db.proposals.orderBy('updatedAt').reverse().toArray()) || [];
  const settings = useLiveQuery(() => db.settings.get('global')) || {};

  const [searchTerm, setSearchTerm] = useState('');
  const [publishingId, setPublishingId] = useState(null);

  const filteredProposals = useMemo(() => {
    if (!searchTerm.trim()) {
      return proposals;
    }
    const ranked = rankProposalSearchResults(proposals, settings, searchTerm);
    return ranked.map((r) => r.proposal);
  }, [proposals, settings, searchTerm]);

  const handleDelete = async (id, e) => {
    e.preventDefault();
    const isConfirmed = await confirm({
      title: 'Delete Statement of Work?',
      message: 'This action is permanent and cannot be undone. All data for this SOW will be lost.',
      type: 'danger',
      confirmText: 'Delete Forever',
      cancelText: 'Keep Document',
    });

    if (isConfirmed) {
      try {
        const result = await deleteProposalSafely(id, settings);
        if (result.cloudSyncPending) {
          showToast('Document deleted locally. Cloud removal is pending and will retry on the next successful sync.', 'error');
        } else {
          showToast('Document Deleted Permanently');
        }
      } catch (err) {
        showToast(`Delete failed: ${err.message}`, 'error');
      }
    }
  };

  const handleCreateNew = async () => {
    const name = await promptText({
      title: 'Name your Statement of Work',
      message: 'Enter a clear internal and client-facing name for this SOW.',
      label: 'SOW name',
      placeholder: 'ASG-Operations_Job-Costing',
      helperText: 'Letters, numbers, spaces, hyphens and underscores only.',
      confirmText: 'Create SOW',
      cancelText: 'Cancel',
      maxLength: 120,
      validate: validateSowName,
    });

    if (!name) return;
    navigate('/proposals/new', { state: { initialSowTitle: name } });
  };

  const handlePublishFromLibrary = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();

    const prop = await db.proposals.get(id);
    if (!prop) {
      showToast('Proposal not found', 'error');
      return;
    }

    if (isPublishedStatus(prop.status)) {
      showToast('This SOW is already published', 'error');
      return;
    }

    const isConfirmed = await confirm({
      title: 'Publish Statement of Work?',
      message: `This will publish "${prop.title || 'Untitled SOW'}" using its current saved content.`,
      confirmText: 'Publish SOW',
      cancelText: 'Cancel',
    });

    if (!isConfirmed) return;

    setPublishingId(id);
    try {
      const settingsData = await db.settings.get('global');

      if (!prop.documentNumber && settingsData?.googleBackupConnected && settingsData?.googleBackupUrl) {
        try {
          const res = await reserveDocumentNumber(settingsData, id, settingsData.documentPrefix || 'SOW');
          if (res.documentNumber) {
            prop.documentNumber = res.documentNumber;
            prop.documentNumberAssignedAt = res.documentNumberAssignedAt || new Date().toISOString();
          }
        } catch (err) {
          console.warn('Failed to reserve document number:', err);
        }
      }

      const now = Date.now();
      const currentVersion = prop.version;
      const newVersion = prop.reopenedAt ? incrementMinorVersion(currentVersion) : currentVersion || '1.0';

      const updatedProp = {
        ...prop,
        status: 'Published',
        publishedAt: prop.publishedAt || now,
        updatedAt: now,
        version: newVersion,
      };

      await db.proposals.put(updatedProp);
      showToast('SOW published successfully.');

      if (settingsData?.googleBackupConnected && settingsData?.googleBackupUrl) {
        try {
          await pushWorkspaceBackup(settingsData);
        } catch (err) {
          showToast(`Cloud sync failed: ${err.message}`, 'error');
        }
      }
    } catch (err) {
      showToast(`Publish failed: ${err.message}`, 'error');
    } finally {
      setPublishingId(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">Library</h1>
          <p className="text-gray-500 mt-2 font-medium">Manage and archive your project documentation.</p>
        </div>
        <button
          onClick={handleCreateNew}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg shadow-indigo-100 hover:scale-[1.02] sm:self-end"
        >
          <SafeIcon icon={FiIcons.FiPlus} /> Create New SOW
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 bg-gray-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="relative w-full max-w-xl flex items-center">
            <SafeIcon icon={FiIcons.FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
            <input
              type="text"
              placeholder="Smart search: title, client, email, phone, amount or status…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-16 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              <button
                onClick={() => window.dispatchEvent(new Event('scopeshield:open-proposal-search'))}
                className="text-[10px] font-bold bg-gray-100 text-gray-500 px-2 py-1 rounded border border-gray-200 hover:bg-gray-200 transition-colors"
                title="Open Global Search"
              >
                {navigator.platform?.toUpperCase().indexOf('MAC') >= 0 ? '⌘K' : 'Ctrl K'}
              </button>
            </div>
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest text-indigo-500 shrink-0 bg-indigo-50 px-2.5 py-1 rounded-md border border-indigo-100">
            Fuzzy ranked search
          </span>
        </div>

        <div className="flex-1 overflow-auto">
          {filteredProposals.length === 0 ? (
            <div className="px-6 py-20 text-center text-gray-400 italic">
              No matching documents found in your library.
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <table className="hidden lg:table w-full text-left">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-200 text-[10px] uppercase font-black tracking-widest text-gray-400">
                    <th className="px-6 py-5">SOW</th>
                    <th className="px-6 py-5">Client & Contact</th>
                    <th className="px-6 py-5">Commercial</th>
                    <th className="px-6 py-5">Status</th>
                    <th className="px-6 py-5">Modified</th>
                    <th className="px-6 py-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredProposals.map((prop) => {
                    const total = calculateTotal(prop, settings);
                    const deposit = calculateDeposit(prop, total);
                    const currency = settings?.currency || 'AUD';
                    const phoneDisplay = getDisplayPhone(prop);
                    const phoneHref = normaliseTelHref(phoneDisplay);

                    return (
                      <tr key={prop.id} className="hover:bg-indigo-50/30 transition-all group">
                        <td className="px-6 py-5 align-top">
                          <Link
                            to={`/proposals/${prop.id}`}
                            className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors block"
                          >
                            {prop.title || 'Untitled Proposal'}
                          </Link>
                          {prop.documentNumber && (
                            <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest mt-1 block">
                              {prop.documentNumber} {prop.version && `· v${prop.version}`}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-5 align-top">
                          <div className="text-gray-600 font-medium text-sm">{prop.clientName || '-'}</div>
                          {prop.sections?.pointOfContact && (
                            <div className="text-xs text-gray-500 mt-1 truncate">{prop.sections.pointOfContact}</div>
                          )}
                          {prop.emailAddress && (
                            <a
                              href={`mailto:${prop.emailAddress}`}
                              className="block text-xs text-indigo-600 hover:underline mt-1 truncate"
                            >
                              {prop.emailAddress}
                            </a>
                          )}
                          {phoneDisplay && phoneHref && (
                            <a
                              href={`tel:${phoneHref}`}
                              className="block text-xs text-indigo-600 hover:underline mt-0.5"
                            >
                              {phoneDisplay}
                            </a>
                          )}
                        </td>
                        <td className="px-6 py-5 align-top">
                          {total > 0 ? (
                            <>
                              <div className="text-sm font-bold text-gray-900">
                                {formatCurrency(total, currency)}
                              </div>
                              {deposit && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  Deposit {deposit.percent}% · {formatCurrency(deposit.amount, currency)}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-sm text-gray-400 italic">No commercial value</div>
                          )}
                        </td>
                        <td className="px-6 py-5 align-top">
                          <span
                            className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                              isPublishedStatus(prop.status)
                                ? 'bg-green-100 text-green-800'
                                : 'bg-slate-100 text-slate-800'
                            }`}
                          >
                            {isPublishedStatus(prop.status) ? 'Published' : prop.status || 'Draft'}
                          </span>
                          <ApprovalBadge approval={prop.approval} />
                        </td>
                        <td className="px-6 py-5 align-top text-sm text-gray-500 font-medium">
                          {format(new Date(prop.updatedAt), 'MMM d, yyyy')}
                        </td>
                        <td className="px-6 py-5 align-top text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            <Link
                              to={`/proposals/${prop.id}`}
                              className="p-2.5 text-gray-400 hover:text-indigo-600 bg-white rounded-lg border border-gray-200 shadow-sm transition-all"
                              title="Edit"
                              style={{ minWidth: '36px', minHeight: '36px' }}
                            >
                              <SafeIcon icon={FiIcons.FiEdit3} />
                            </Link>
                            {!isPublishedStatus(prop.status) && (
                              <button
                                onClick={(e) => handlePublishFromLibrary(prop.id, e)}
                                disabled={publishingId === prop.id}
                                className="p-2.5 text-gray-400 hover:text-green-600 bg-white rounded-lg border border-gray-200 shadow-sm transition-all disabled:opacity-50"
                                title="Publish"
                                style={{ minWidth: '36px', minHeight: '36px' }}
                              >
                                <SafeIcon
                                  icon={publishingId === prop.id ? FiIcons.FiRefreshCw : FiIcons.FiLock}
                                  className={publishingId === prop.id ? 'animate-spin' : ''}
                                />
                              </button>
                            )}
                            <button
                              onClick={(e) => handleDelete(prop.id, e)}
                              className="p-2.5 text-gray-400 hover:text-red-600 bg-white rounded-lg border border-gray-200 shadow-sm transition-all"
                              title="Delete"
                              style={{ minWidth: '36px', minHeight: '36px' }}
                            >
                              <SafeIcon icon={FiIcons.FiTrash2} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Mobile/Tablet Stacked Cards */}
              <div className="lg:hidden divide-y divide-gray-100">
                {filteredProposals.map((prop) => {
                  const total = calculateTotal(prop, settings);
                  const deposit = calculateDeposit(prop, total);
                  const currency = settings?.currency || 'AUD';
                  const phoneDisplay = getDisplayPhone(prop);
                  const phoneHref = normaliseTelHref(phoneDisplay);

                  return (
                    <div key={prop.id} className="p-5 hover:bg-gray-50 transition-colors">
                      <Link
                        to={`/proposals/${prop.id}`}
                        className="font-bold text-gray-900 block mb-2"
                      >
                        {prop.title || 'Untitled Proposal'}
                      </Link>

                      {prop.documentNumber && (
                        <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest block mb-3">
                          {prop.documentNumber} {prop.version && `· v${prop.version}`}
                        </span>
                      )}

                      <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                        <div>
                          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">
                            Client
                          </div>
                          <div className="text-gray-600 font-medium">{prop.clientName || '-'}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">
                            Commercial
                          </div>
                          {total > 0 ? (
                            <div className="text-gray-900 font-bold">
                              {formatCurrency(total, currency)}
                            </div>
                          ) : (
                            <div className="text-gray-400 italic text-xs">No commercial value</div>
                          )}
                          {deposit && (
                            <div className="text-xs text-gray-500">
                              Deposit {deposit.percent}% · {formatCurrency(deposit.amount, currency)}
                            </div>
                          )}
                        </div>
                      </div>

                      {(prop.sections?.pointOfContact || prop.emailAddress || (phoneDisplay && phoneHref)) && (
                        <div className="flex flex-col gap-1 mb-3">
                          {prop.sections?.pointOfContact && (
                            <span className="text-sm text-gray-600">{prop.sections.pointOfContact}</span>
                          )}
                          {prop.emailAddress && (
                            <a
                              href={`mailto:${prop.emailAddress}`}
                              className="text-sm text-indigo-600 hover:underline"
                            >
                              {prop.emailAddress}
                            </a>
                          )}
                          {phoneDisplay && phoneHref && (
                            <a
                              href={`tel:${phoneHref}`}
                              className="text-sm text-indigo-600 hover:underline"
                            >
                              {phoneDisplay}
                            </a>
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-2 flex-wrap mb-4">
                        <span
                          className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                            isPublishedStatus(prop.status)
                              ? 'bg-green-100 text-green-800'
                              : 'bg-slate-100 text-slate-800'
                          }`}
                        >
                          {isPublishedStatus(prop.status) ? 'Published' : prop.status || 'Draft'}
                        </span>
                        <ApprovalBadge approval={prop.approval} />
                      </div>

                      <div className="flex items-center justify-between gap-2 pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-400 font-medium">
                          {format(new Date(prop.updatedAt), 'MMM d, yyyy')}
                        </span>
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/proposals/${prop.id}`}
                            className="p-2.5 text-gray-400 hover:text-indigo-600 bg-white rounded-lg border border-gray-200 shadow-sm transition-all"
                            title="Edit"
                            style={{ minWidth: '36px', minHeight: '36px' }}
                          >
                            <SafeIcon icon={FiIcons.FiEdit3} />
                          </Link>
                          {!isPublishedStatus(prop.status) && (
                            <button
                              onClick={(e) => handlePublishFromLibrary(prop.id, e)}
                              disabled={publishingId === prop.id}
                              className="p-2.5 text-gray-400 hover:text-green-600 bg-white rounded-lg border border-gray-200 shadow-sm transition-all disabled:opacity-50"
                              title="Publish"
                              style={{ minWidth: '36px', minHeight: '36px' }}
                            >
                              <SafeIcon
                                icon={publishingId === prop.id ? FiIcons.FiRefreshCw : FiIcons.FiLock}
                                className={publishingId === prop.id ? 'animate-spin' : ''}
                              />
                            </button>
                          )}
                          <button
                            onClick={(e) => handleDelete(prop.id, e)}
                            className="p-2.5 text-gray-400 hover:text-red-600 bg-white rounded-lg border border-gray-200 shadow-sm transition-all"
                            title="Delete"
                            style={{ minWidth: '36px', minHeight: '36px' }}
                          >
                            <SafeIcon icon={FiIcons.FiTrash2} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}